/**
 * speech.js
 *
 * Priority:
 *   1. Uploaded audio file (admin-recorded)  — instant, human voice
 *   2. Browser TTS fallback                  — always available
 *
 * Startup flow (called from WordsContext):
 *   preloadManifest()  →  fetches /api/audio/manifest once
 *                      →  populates _uploadedKeys Set  (O(1) lookups forever)
 *                      →  preloads Audio objects for every uploaded file
 *                         (browser HTTP-caches them; letters/nikud play instantly)
 *
 * Runtime:
 *   speakHe(text, { audioId }) / speakEn(text, { audioId })
 *     → Prefer stable DB key from audioId (e.g. word-123, letter-name-5), then legacy audioKey(text)
 *     → synchronous lookup in manifest Set
 */

import { getApiBase } from "../api/apiBase";

// ── Manifest / uploaded-key registry ─────────────────────────────────────────

let _uploadedKeys = null;          // null = not yet fetched; Set<string> after fetch
/** Resolves when manifest fetch + key set are done (concurrent callers share one load). */
let _manifestPromise = null;
/** Bumped on invalidate so stale in-flight fetches cannot overwrite fresh state. */
let _manifestGeneration = 0;
// Preloaded Audio elements keyed by audioKey — reused on every play
const _audioPool = new Map();      // key → HTMLAudioElement

/** Normalise any text to a legacy filename key (strip nikud, spaces→underscore) */
export function audioKey(text) {
  return text.replace(/[\u05B0-\u05C7]/g, "").trim().replace(/\s+/g, "_");
}

/** Keys to try for playback: stable id-based first, then legacy text key (existing uploads). */
export function resolveAudioKeys(text, audioId) {
  const legacy = audioKey(text);
  const keys = [];
  if (audioId && String(audioId).trim()) keys.push(String(audioId).trim());
  if (legacy && !keys.includes(legacy)) keys.push(legacy);
  return keys;
}

/** Public URL for an uploaded audio file */
export function audioUrl(key) {
  return `${getApiBase()}/audio/file/${encodeURIComponent(key)}`;
}

/**
 * Fetch manifest from DB and register keys. Letter/nikud entries are listed first by the API.
 * Returns a Promise — MUST be awaited at startup so first speakHe() sees uploaded keys (not TTS).
 */
export function preloadManifest() {
  if (_uploadedKeys !== null) return Promise.resolve();
  if (_manifestPromise) return _manifestPromise;

  const gen = _manifestGeneration;
  _manifestPromise = (async () => {
    try {
      const res = await fetch(`${getApiBase()}/audio/manifest`);
      if (!res.ok) throw new Error("manifest fetch failed");
      const keys = await res.json(); // string[] — letter/nikud first
      if (gen !== _manifestGeneration) return;
      _uploadedKeys = new Set(keys);

      keys.forEach((key) => {
        if (!key || _audioPool.has(key)) return;
        const a = new Audio(audioUrl(key));
        a.preload = "auto";
        a.load();
        _audioPool.set(key, a);
      });
    } catch {
      if (gen === _manifestGeneration) _uploadedKeys = new Set();
    } finally {
      _manifestPromise = null;
    }
  })();

  return _manifestPromise;
}

/** Called by admin panel after upload/delete to sync the in-memory registry */
export function invalidateAudioCache(key) {
  if (key) _audioPool.delete(key);
  else _audioPool.clear();
  _manifestGeneration += 1;
  _uploadedKeys = null;
  _manifestPromise = null;
  void preloadManifest();
}

// ── Voice selection (TTS fallback) ────────────────────────────────────────────

let _heVoice = null;
let _enVoice = null;
let _voicesReady = false;

function _scoreHe(v) {
  let s = 0;
  if (v.name.includes("Sivan"))    s += 40;
  if (v.name.includes("Carmit"))   s += 35;
  if (v.name.includes("Yoav"))     s += 30;
  if (v.name.includes("Tami"))     s += 28;
  if (v.name.includes("Neural2"))  s += 25;
  if (v.name.includes("Wavenet"))  s += 20;
  if (v.name.includes("Enhanced")) s += 15;
  if (v.name.includes("Premium"))  s += 15;
  if (v.name.includes("Natural"))  s += 10;
  if (v.lang === "he-IL")          s += 20;
  else if (v.lang.startsWith("he")) s += 10;
  if (v.localService)              s += 5;
  return s;
}

function _scoreEn(v) {
  let s = 0;
  if (v.name.includes("Samantha")) s += 30;
  if (v.name.includes("Karen"))    s += 28;
  if (v.name.includes("Moira"))    s += 25;
  if (v.name.includes("Neural2"))  s += 25;
  if (v.name.includes("Wavenet"))  s += 20;
  if (v.name.includes("Enhanced")) s += 15;
  if (v.name.includes("Premium"))  s += 15;
  if (v.lang === "en-US")          s += 15;
  else if (v.lang === "en-GB")     s += 12;
  else if (v.lang.startsWith("en")) s += 8;
  if (v.localService)              s += 5;
  return s;
}

function _pickVoices() {
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return;
  const he = voices.filter(v => v.lang.startsWith("he"));
  const en = voices.filter(v => v.lang.startsWith("en"));
  if (he.length) _heVoice = he.sort((a, b) => _scoreHe(b) - _scoreHe(a))[0];
  if (en.length) _enVoice = en.sort((a, b) => _scoreEn(b) - _scoreEn(a))[0];
  _voicesReady = true;
}

if (typeof window !== "undefined" && window.speechSynthesis) {
  _pickVoices();
  window.speechSynthesis.addEventListener("voiceschanged", _pickVoices);
}

function _tts(text, { lang = "he-IL", rate = 0.72, pitch = 1.2, onEnd } = {}) {
  if (!window.speechSynthesis) { onEnd?.(); return; }
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = lang; u.rate = rate; u.pitch = pitch; u.volume = 1.0;
  if (onEnd) u.onend = onEnd;
  const isHe = lang.startsWith("he");
  const voice = isHe ? _heVoice : _enVoice;
  if (voice) u.voice = voice;
  if (!_voicesReady && !voice) {
    const retry = () => {
      _pickVoices();
      const v2 = isHe ? _heVoice : _enVoice;
      if (v2) u.voice = v2;
      window.speechSynthesis.speak(u);
      window.speechSynthesis.removeEventListener("voiceschanged", retry);
    };
    window.speechSynthesis.addEventListener("voiceschanged", retry);
  }
  window.speechSynthesis.speak(u);
}

// ── Core playback ─────────────────────────────────────────────────────────────

function _pickKeyForPlay(text, audioId) {
  const keys = resolveAudioKeys(text, audioId);
  for (const k of keys) {
    if (_uploadedKeys?.has(k)) return k;
  }
  return null;
}

function _playFile(key, text, ttsOpts) {
  let a = _audioPool.get(key);
  if (!a) {
    a = new Audio(audioUrl(key));
    _audioPool.set(key, a);
  }
  a.currentTime = 0;
  a.onended = ttsOpts.onEnd || null;
  a.play().catch(() => _tts(text, ttsOpts));
}

function _play(text, ttsOpts) {
  const { audioId, ...playOpts } = ttsOpts;

  const tryPlay = () => {
    const key = _pickKeyForPlay(text, audioId);
    if (key) {
      _playFile(key, text, playOpts);
      return true;
    }
    return false;
  };

  if (_uploadedKeys === null && _manifestPromise) {
    _manifestPromise.then(() => {
      if (!tryPlay()) _tts(text, playOpts);
    });
    return;
  }

  if (!tryPlay()) _tts(text, playOpts);
}

// ── Public API ────────────────────────────────────────────────────────────────

export function speak(text, opts = {}) {
  _play(text, { lang: "he-IL", rate: 0.72, pitch: 1.2, ...opts });
}

export const speakHe = (text, opts = {}) =>
  _play(text, { lang: "he-IL", rate: 0.72, pitch: 1.2, ...opts });

export const speakEn = (text, opts = {}) =>
  _play(text, { lang: "en-US", rate: 0.80, pitch: 1.1, ...opts });
